"use client";

/* eslint-disable @next/next/no-img-element -- static Figma vectors at their exact intrinsic sizes */

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { GROQImage } from "@/sanity/lib/definitions";
import { Trademania2Video } from "@/sanity/pages/PageTrademania2Template";
import { Artboard, CountdownParts, DEFAULT_EVENT_DATE, onScrollFrame, prefersReducedMotion, useCountdown } from "@/components/trademania-2/shared";

import x1 from "@/public/trademania-2/k2-x1.svg";
import x2 from "@/public/trademania-2/k2-x2.svg";
import x3 from "@/public/trademania-2/k2-x3.svg";
import x4 from "@/public/trademania-2/k2-x4.svg";
import revealRing from "@/public/trademania-2/k3-ring.png";
import revealDisc from "@/public/trademania-2/k3-ellipse557.png";
import revealE406 from "@/public/trademania-2/k3-e406.svg";
import revealE390 from "@/public/trademania-2/k3-e390.svg";
import revealE577 from "@/public/trademania-2/k3-e577.svg";

/* ------------------------------------------------------------- 02 · platform */

const PLATFORM_BG =
  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 634.62 474.8' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='1'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(0.0000044842 106 -250.81 -1.185 317.77 -396.43)'><stop stop-color='rgba(0,0,0,1)' offset='0'/><stop stop-color='rgba(0,0,0,1)' offset='0.2'/><stop stop-color='rgba(0,0,0,1)' offset='0.37286'/><stop stop-color='rgba(11,12,9,1)' offset='0.39147'/><stop stop-color='rgba(21,25,18,1)' offset='0.41008'/><stop stop-color='rgba(43,50,36,1)' offset='0.44729'/><stop stop-color='rgba(64,74,54,1)' offset='0.4845'/><stop stop-color='rgba(86,99,72,1)' offset='0.52172'/><stop stop-color='rgba(128,149,108,1)' offset='0.59615'/><stop stop-color='rgba(171,198,144,1)' offset='0.67057'/><stop stop-color='rgba(209,223,195,1)' offset='0.83529'/><stop stop-color='rgba(247,248,246,1)' offset='1'/></radialGradient></defs></svg>\")";

const CROSS = [
  { src: x1, left: 141.33, top: 165.82, w: 46.559, h: 90.698, inset: [-1.1, -2.15] },
  { src: x2, left: 145.27, top: 167.49, w: 44.017, h: 84.256, inset: [-1.19, -2.27] },
  { src: x3, left: 155.21, top: 176.71, w: 37.944, h: 61.335, inset: [-1.63, -2.64] },
  { src: x4, left: 137.98, top: 185.75, w: 42.993, h: 47.568, inset: [-2.1, -2.33] },
];

const PLATFORM_H = 474.804;

/**
 * Card 02: the platform on a MacBook over the radial green glow; the motion video, when set, plays over it.
 * `height` (artboard px) makes the board taller: the glow stretches, the MacBook keeps its size and
 * place and the video keeps its own frame at the top.
 */
export function PlatformVisual({
  image,
  video,
  height = PLATFORM_H,
  className,
}: {
  image?: GROQImage;
  video?: Trademania2Video;
  height?: number;
  className?: string;
}) {
  return (
    <Artboard
      width={634.621}
      height={height}
      // clip-path also clips the video layer, which border-radius alone may leave square
      className={cn("rounded-[4px] [clip-path:inset(0_round_4px)]", className)}>
      <div
        className="relative h-full w-full"
        style={{ backgroundImage: PLATFORM_BG }}>
        {image?.src && (
          <Image
            src={image}
            alt=""
            sizes="(max-width: 992px) 100vw, 60vw"
            className="absolute top-[-129.93px] left-[-269.23px] h-[821px] w-[1231px] max-w-none object-cover"
          />
        )}
        {video?.url && (
          // scaled 1% from the left so the dark column on the clip's right edge falls outside
          <video
            className="absolute inset-x-0 top-0 w-full origin-left scale-[1.01] object-cover"
            style={{ height: PLATFORM_H }}
            src={video.url}
            autoPlay
            muted
            loop
            playsInline
          />
        )}
      </div>
    </Artboard>
  );
}

/* --------------------------------------------------------------- 03 · reveal */

const REVEAL_UNITS: [keyof CountdownParts, string][] = [
  ["d", "Tage"],
  ["h", "Std"],
  ["m", "Min"],
  ["s", "Sek"],
];

/** Card 03: eclipse rings with the live countdown. */
export function RevealVisual({
  label,
  date,
  target,
  className,
}: {
  label?: string;
  date?: string;
  target?: string;
  className?: string;
}) {
  const parts = useCountdown(target);
  const progressRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const ring = progressRef.current;
    if (!ring) return;
    const end = new Date(target || DEFAULT_EVENT_DATE).getTime();
    const update = () => {
      const remaining = Math.max(0, end - Date.now());
      const progress = remaining > 0 ? 1 - (remaining % 60000) / 60000 : 1;
      const mask = `conic-gradient(#000 ${progress * 360}deg, transparent 0deg)`;
      ring.style.maskImage = mask;
      ring.style.webkitMaskImage = mask;
    };
    update();
    if (prefersReducedMotion()) {
      const timer = window.setInterval(update, 1000);
      return () => window.clearInterval(timer);
    }
    return onScrollFrame(update);
  }, [target]);

  return (
    <Artboard
      width={634.133}
      height={474.439}
      className={cn("rounded-[4px] bg-black", className)}>
      <div className="relative h-full w-full font-geist">
        <div className="absolute top-[-24.01px] left-[56.57px] flex h-[520.688px] w-[520.688px] items-center justify-center">
          <div className="relative h-[520.688px] w-[520.688px] -rotate-90">
            <img
              src={revealRing.src}
              alt=""
              width={512.357}
              height={512.357}
              className="absolute inset-[.8%] block h-[98.4%] w-[98.4%] max-w-none"
            />
          </div>
        </div>
        <img
          src={revealE406.src}
          alt=""
          className="absolute top-[calc(50%-.38px)] left-[calc(50%+.35px)] h-[406px] w-[406px] max-w-none -translate-x-1/2 -translate-y-1/2"
        />
        <img
          src={revealE390.src}
          alt=""
          className="absolute top-[calc(50%-.38px)] left-[calc(50%+.35px)] h-[390px] w-[390px] max-w-none -translate-x-1/2 -translate-y-1/2"
        />

        <div className="absolute top-[161.33px] left-[155.41px] flex w-[323px] flex-col items-center gap-[24px]">
          <div className="flex items-center gap-[10px]">
            <span className="h-[6px] w-[6px] rounded-[3px] bg-[#F9F2B9]" />
            <p className="text-center text-[14px] leading-[17.085px] font-medium whitespace-nowrap text-white/80 uppercase">
              {label}
            </p>
          </div>
          <div className="flex items-start justify-center gap-[15px]">
            {REVEAL_UNITS.map(([key, unit], i) => (
              <React.Fragment key={key}>
                {i > 0 && (
                  <div className="flex items-start self-stretch pt-[4px] pb-[22px]">
                    <div className="h-full w-px bg-white/[.08]" />
                  </div>
                )}
                <div className="flex min-w-[48px] flex-col items-center gap-[8px]">
                  <p className="text-center font-bebas text-[70.921px] leading-[63.829px] tracking-[-2.8369px] whitespace-nowrap text-white">
                    {parts[key]}
                  </p>
                  <p className="text-center text-[14px] leading-[14.644px] tracking-[2.3431px] whitespace-nowrap text-white/[.45] uppercase">
                    {unit}
                  </p>
                </div>
              </React.Fragment>
            ))}
          </div>
          <p className="text-center text-[16px] leading-[17.899px] whitespace-nowrap text-white/[.55]">{date}</p>
        </div>

        <img
          src={revealDisc.src}
          alt=""
          width={557}
          height={557}
          className="absolute top-[calc(50%+.12px)] left-[calc(50%-.15px)] h-[557px] w-[557px] max-w-none -translate-x-1/2 -translate-y-1/2"
        />
        <div aria-hidden className="pointer-events-none absolute top-[calc(50%+.28px)] left-[calc(50%+.43px)] h-[577px] w-[577px] -translate-x-1/2 -translate-y-1/2">
          <img
            src={revealE577.src}
            alt=""
            className="absolute inset-0 h-full w-full max-w-none"
          />
          <svg
            ref={progressRef}
            viewBox="0 0 577 577"
            className="absolute inset-0 h-full w-full text-[#F9F2B9]">
            <circle cx="288.5" cy="288.5" r="288" fill="none" stroke="currentColor" />
            <circle
              cx="288.5"
              cy="288.5"
              r="270"
              fill="none"
              stroke="currentColor"
              strokeWidth="12"
              pathLength="60"
              strokeDasharray="0.06 0.94"
              transform="rotate(-90 288.5 288.5)"
            />
          </svg>
        </div>
      </div>
    </Artboard>
  );
}
